import React, { useEffect, useState, useCallback } from "react";
import { Modal, Textarea, Loader, Group, Drawer, Menu } from "@mantine/core";
import moment from "moment";
import swal from "sweetalert";
import Icon from "./icons";
import schedulerService from "../../../services/scheduler";
import { photoOf } from "./scheduleUtils";
import css from "./Scheduler.module.css";

// Staff request time off (PTO/vacation/sick/…); admin approves or denies with a
// reason and can post the staff's conflicting shifts as open shifts. One
// role-aware panel drives both the staff "My Time Off" tab and the admin
// "Time Off" view. Data + notifications are handled by the backend.
// Display labels for a request's type. Covers the current set (pto/medical/
// unpaid) plus legacy values so older records still render.
const TYPE_LABEL = { pto: "PTO", medical: "Medical leave", unpaid: "Unpaid leave", vacation: "Vacation", sick: "Sick leave", other: "Other" };
const STATUS = {
  pending: { cls: "amber", label: "Pending" },
  approved: { cls: "green", label: "Approved" },
  denied: { cls: "red", label: "Denied" },
  cancelled: { cls: "grey", label: "Cancelled" },
};
const idOf = (r) => r._id || r.id;
// Legacy reasons were stored as "Reason — description"; normalize the em dash to
// a colon at display time so no em dash ever renders (new requests use ": ").
const cleanReason = (s) => (s || "").replace(/\s*—\s*/g, ": ");
const fmtRange = (a, b) => {
  const f = (d) => moment.utc(d).format("MMM D, YYYY");
  const s = f(a);
  const e = f(b);
  return s === e ? s : `${s} – ${e}`;
};
// The concrete days a request covers, as sorted UTC moments — prefers the
// explicit `dates` array, falling back to the start/end envelope for legacy rows.
const daysOf = (r) => (Array.isArray(r.dates) && r.dates.length ? [...r.dates].map((d) => moment.utc(d)).sort((a, b) => a - b) : null);
// Label: single day, a range when continuous, or a comma list (+N) when scattered.
const fmtReqDates = (r) => {
  const ds = daysOf(r);
  if (!ds) return fmtRange(r.startDate, r.endDate);
  if (ds.length === 1) return ds[0].format("MMM D, YYYY");
  const consecutive = ds.every((m, i) => i === 0 || m.diff(ds[i - 1], "days") === 1);
  if (consecutive) return `${ds[0].format("MMM D")} – ${ds[ds.length - 1].format("MMM D, YYYY")}`;
  const shown = ds.slice(0, 2).map((m) => m.format("MMM D")).join(", ");
  return `${shown}${ds.length > 2 ? ` +${ds.length - 2}` : ""}`;
};
const dayCount = (a, b) => Math.max(1, moment.utc(b).startOf("day").diff(moment.utc(a).startOf("day"), "days") + 1);
const dayCountOf = (r) => { const ds = daysOf(r); return ds ? ds.length : dayCount(r.startDate, r.endDate); };
const initials = (n) => (n || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

export default function TimeOffPanel({ mode = "staff", tabs = null, onCountChange, onRequest, reloadSignal, from = null, to = null }) {
  const isAdmin = mode === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // admin deny modal
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyReason, setDenyReason] = useState("");
  // Clicking a row opens this request's detail side panel.
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await schedulerService.getTimeOff();
    const list = Array.isArray(data) ? data : [];
    setRows(list);
    onCountChange?.(list.filter((r) => r.status === "pending").length);
    setLoading(false);
  }, [onCountChange]);
  useEffect(() => { load(); }, [load, reloadSignal]);

  const approve = async (r) => { setBusy(true); await schedulerService.decideTimeOff(idOf(r), { status: "approved" }); setBusy(false); load(); };
  const confirmDeny = async () => {
    setBusy(true);
    await schedulerService.decideTimeOff(idOf(denyTarget), { status: "denied", denialReason: denyReason.trim() });
    setBusy(false); setDenyTarget(null); setDenyReason(""); load();
  };
  const cancelReq = async (r) => { setBusy(true); await schedulerService.cancelTimeOff(idOf(r)); setBusy(false); load(); };
  const postOpen = async (r) => {
    setBusy(true);
    const res = await schedulerService.convertTimeOffOpenShifts(idOf(r));
    setBusy(false);
    swal(
      res?.count ? "Shifts posted" : "No conflicting shifts",
      res?.count ? `${res.count} shift${res.count === 1 ? "" : "s"} posted as open for coverage.` : "This staff member has no scheduled shifts during that period.",
      res?.count ? "success" : "info"
    );
    load();
  };

  // The page is grounded on a weekly view: show only requests that overlap the
  // selected week. The tile count (onCountChange) still reflects ALL pending
  // requests so actionable items in other weeks aren't hidden.
  const inWeek = useCallback(
    (r) => {
      if (!from || !to) return true;
      const ws = moment.utc(from).startOf("day");
      const we = moment.utc(to).endOf("day");
      const ds = daysOf(r);
      if (ds) return ds.some((m) => m.isSameOrAfter(ws) && m.isSameOrBefore(we));
      return moment.utc(r.startDate).isSameOrBefore(we) && moment.utc(r.endDate).isSameOrAfter(ws);
    },
    [from, to]
  );

  // Pending first, then most recent start date.
  const sorted = rows.filter(inWeek).sort((a, b) => (b.status === "pending") - (a.status === "pending") || new Date(b.startDate) - new Date(a.startDate));

  return (
    <div>
      <div className={css.tblCard} style={{ minHeight: "calc(100vh - 240px)", display: "flex", flexDirection: "column" }}>
        <div className={css.tblHead}>
          {tabs || <h3 style={{ margin: 0 }}>{isAdmin ? "Time-off Requests" : "My Time Off"}</h3>}
          {!isAdmin && (
            <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} style={{ height: 36 }} onClick={() => onRequest?.()}>
              <Icon name="plus" size={16} /> Request Time Off
            </button>
          )}
        </div>
        <div className={css.tblScroll} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {loading ? (
            <Group position="center" style={{ flex: 1 }}><Loader color="var(--app-primary)" /></Group>
          ) : sorted.length === 0 ? (
            <div className={css.empty2} style={{ flex: 1, justifyContent: "center" }}>
              <Icon name="calendar" size={38} />
              <h4>{from && to ? "No time-off requests this week" : "No time-off requests"}</h4>
              <p>{isAdmin ? "Requests from your staff will appear here." : "Request time off and track approvals here."}</p>
            </div>
          ) : (
            <table className={css.tbl}>
              <thead>
                <tr>
                  {isAdmin ? <th>Staff</th> : null}
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Duration</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const st = STATUS[r.status] || STATUS.pending;
                  const days = dayCountOf(r);
                  return (
                    <tr key={idOf(r)} className={css.click} onClick={() => setDetail(r)}>
                      {isAdmin ? (
                        <td>
                          <div className={css.cellStaff}>
                            <span className={css.cellAv}>
                              {r.staff?.photo ? <img src={photoOf(r.staff.photo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : initials(r.staff?.name)}
                            </span>
                            <span className={css.cellNm}>{r.staff?.name || "Staff"}</span>
                          </div>
                        </td>
                      ) : null}
                      <td><span className={`${css.badge2} ${css.blue}`}>{TYPE_LABEL[r.type] || r.type}</span></td>
                      <td className={css.numStrong}>{fmtReqDates(r)}</td>
                      <td>{days} day{days > 1 ? "s" : ""}</td>
                      <td style={{ maxWidth: 240, color: "var(--app-text-muted)" }}>{cleanReason(r.reason) || "-"}</td>
                      <td>
                        <span className={`${css.badge2} ${css[st.cls]}`}>{st.label}</span>
                        {r.status === "denied" && r.denialReason ? <div style={{ fontSize: 11.5, color: "#b42318", marginTop: 3 }}>{r.denialReason}</div> : null}
                        {isAdmin && r.status === "approved" && r.coverageResolved ? <div style={{ fontSize: 11.5, color: "#166534", marginTop: 3 }}>Coverage posted</div> : null}
                      </td>
                      <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const items = [];
                          if (isAdmin && r.status === "pending") {
                            items.push(<Menu.Item key="approve" onClick={() => approve(r)}>Approve</Menu.Item>);
                            items.push(<Menu.Item key="deny" color="red" onClick={() => setDenyTarget(r)}>Deny</Menu.Item>);
                          }
                          if (isAdmin && r.status === "approved" && !r.coverageResolved) {
                            items.push(<Menu.Item key="open" onClick={() => postOpen(r)}>Post open shifts</Menu.Item>);
                          }
                          if (!isAdmin && r.status === "pending") {
                            items.push(<Menu.Item key="cancel" color="red" onClick={() => cancelReq(r)}>Cancel request</Menu.Item>);
                          }
                          if (!items.length) return <span style={{ color: "var(--app-text-muted)", fontSize: 12.5 }}>-</span>;
                          return (
                            <Menu shadow="md" width={170} position="bottom-end" withinPortal radius={10}
                              styles={{
                                dropdown: { padding: 5, border: "1px solid var(--app-border)", boxShadow: "0 8px 24px -6px rgba(16,24,40,0.16)" },
                                item: { fontSize: 13, fontWeight: 500, padding: "8px 11px", borderRadius: 7 },
                              }}
                            >
                              <Menu.Target>
                                <button type="button" className={css.kebabBtn} aria-label="Actions" disabled={busy}><Icon name="dotsV" size={16} /></button>
                              </Menu.Target>
                              <Menu.Dropdown>{items}</Menu.Dropdown>
                            </Menu>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal opened={!!denyTarget} onClose={() => { setDenyTarget(null); setDenyReason(""); }} title={`Deny ${denyTarget?.staff?.name || ""}'s request`} centered radius={14} zIndex={500}>
        <Textarea label="Reason for denial" description="Shared with the staff member." value={denyReason} onChange={(e) => setDenyReason(e.currentTarget.value)} minRows={3} autosize placeholder="e.g. Short-staffed that week." />
        <Group position="right" mt={16}>
          <button type="button" className={css.btnG} onClick={() => { setDenyTarget(null); setDenyReason(""); }} disabled={busy}>Cancel</button>
          <button type="button" className={css.btnP} onClick={confirmDeny} disabled={busy || !denyReason.trim()}>Deny request</button>
        </Group>
      </Modal>

      {/* Request detail side panel */}
      <Drawer
        opened={!!detail}
        onClose={() => setDetail(null)}
        position="right"
        size={460}
        padding={0}
        withCloseButton={false}
        title={null}
        classNames={{ content: "sdDrawerContent" }}
        styles={{ body: { height: "100%", padding: 0 } }}
        overlayProps={{ opacity: 0.5, blur: 3 }}
        zIndex={1000}
      >
        {detail ? (() => {
          const st = STATUS[detail.status] || STATUS.pending;
          const ds = daysOf(detail);
          const dayList = ds ? ds.map((m) => m.format("ddd, MMM D, YYYY")) : [fmtRange(detail.startDate, detail.endDate)];
          const days = dayCountOf(detail);
          return (
            <div className={css.dwWrap}>
              <div className={css.dwHead}>
                <div>
                  <h3 className={css.dwTitle}>Time-off request</h3>
                  <div className={css.dwSub}>{TYPE_LABEL[detail.type] || detail.type} · {days} day{days > 1 ? "s" : ""}</div>
                </div>
                <button type="button" className={css.dwClose} onClick={() => setDetail(null)} aria-label="Close"><Icon name="x" size={16} /></button>
              </div>
              <div className={css.dwBody}>
                <div className={css.dBanner} style={{ background: "var(--app-surface)", border: "1px solid var(--app-border)" }}>
                  {isAdmin ? (
                    <span className={css.bAv}>
                      {detail.staff?.photo ? <img src={photoOf(detail.staff.photo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : initials(detail.staff?.name)}
                    </span>
                  ) : null}
                  <div style={{ flex: 1 }}>
                    <div className={css.bNm}>{isAdmin ? detail.staff?.name || "Staff" : TYPE_LABEL[detail.type] || detail.type}</div>
                    <div className={css.bSub}>{isAdmin ? TYPE_LABEL[detail.type] || detail.type : "Your request"}</div>
                  </div>
                  <span className={`${css.badge2} ${css[st.cls]}`}>{st.label}</span>
                </div>

                <section className={css.dwSection}>
                  <h5 className={css.dwSectionH}>Dates ({days} day{days > 1 ? "s" : ""})</h5>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {dayList.map((d, i) => <span key={i} className={`${css.badge2} ${css.blue}`}>{d}</span>)}
                  </div>
                </section>

                <section className={css.dwSection}>
                  <h5 className={css.dwSectionH}>Reason</h5>
                  <div style={{ fontSize: 13, color: "var(--app-text)" }}>{cleanReason(detail.reason) || "No reason provided."}</div>
                </section>

                {detail.status === "denied" && detail.denialReason ? (
                  <section className={css.dwSection}>
                    <h5 className={css.dwSectionH} style={{ color: "#b42318" }}>Reason for denial</h5>
                    <div style={{ fontSize: 13, color: "#b42318" }}>{detail.denialReason}</div>
                  </section>
                ) : null}

                {isAdmin && detail.status === "approved" && detail.coverageResolved ? (
                  <div className={css.excClear}><Icon name="check" size={15} /> Coverage posted as open shifts.</div>
                ) : null}
              </div>
              <div className={css.dwFoot}>
                {isAdmin && detail.status === "pending" ? (
                  <>
                    <button type="button" className={css.btnG} style={{ color: "#b42318", borderColor: "var(--app-border)" }} onClick={() => { setDenyTarget(detail); setDetail(null); }} disabled={busy}>Deny</button>
                    <span style={{ flex: 1 }} />
                    <button type="button" className={css.btnP} onClick={() => { approve(detail); setDetail(null); }} disabled={busy}><Icon name="check" size={15} /> Approve</button>
                  </>
                ) : isAdmin && detail.status === "approved" && !detail.coverageResolved ? (
                  <>
                    <span style={{ flex: 1 }} />
                    <button type="button" className={css.btnP} onClick={() => { postOpen(detail); setDetail(null); }} disabled={busy}>Post open shifts</button>
                  </>
                ) : !isAdmin && detail.status === "pending" ? (
                  <>
                    <span style={{ flex: 1 }} />
                    <button type="button" className={css.btnG} style={{ color: "#b42318", borderColor: "var(--app-border)" }} onClick={() => { cancelReq(detail); setDetail(null); }} disabled={busy}>Cancel request</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1 }} />
                    <button type="button" className={css.btnG} onClick={() => setDetail(null)}>Close</button>
                  </>
                )}
              </div>
            </div>
          );
        })() : null}
      </Drawer>
    </div>
  );
}
